<template>
  <div style="display:grid;gap:1.5rem;">
    <el-card shadow="never">
      <template #header>
        <span class="card-title">Conversations</span>
      </template>

      <el-alert
        title="Lecture seule — consultation des échanges client / artisan pour modération ou audit."
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom:1.25rem;"
      />

      <el-input
        v-model="searchQuery"
        placeholder="Rechercher par nom, téléphone, CNIB…"
        clearable
        size="large"
        @input="onSearchInput"
        @clear="onSearchInput"
      >
        <template #prefix><el-icon><Search /></el-icon></template>
      </el-input>

      <div v-if="searchResults.length" style="margin-top:1rem;display:flex;flex-direction:column;gap:.75rem;">
        <el-card
          v-for="item in searchResults"
          :key="item.intervention_id"
          shadow="hover"
          style="cursor:pointer;"
          @click="selectIntervention(item.intervention_id)"
        >
          <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.5rem;">
            <div style="display:flex;flex-direction:column;gap:.25rem;flex:1;min-width:200px;">
              <div style="display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;">
                <span style="font-weight:700;font-size:.9rem;">{{ item.artisan_name || 'Artisan' }}</span>
                <span style="font-size:.8rem;color:var(--el-text-color-secondary);">{{ item.artisan_phone || '' }}</span>
                <el-tag size="small" type="primary">Artisan</el-tag>
              </div>
              <div style="display:flex;align-items:center;gap:.5rem;flex-wrap:wrap;">
                <span style="font-weight:700;font-size:.9rem;">{{ item.client_name || 'Client' }}</span>
                <span style="font-size:.8rem;color:var(--el-text-color-secondary);">{{ item.client_phone || '' }}</span>
                <el-tag size="small" type="success">Client</el-tag>
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:.75rem;flex-shrink:0;">
              <el-tag :type="interventionTagType(item.status)" size="small">{{ item.status || '—' }}</el-tag>
              <span style="font-size:.8rem;color:var(--el-text-color-secondary);">{{ fmtDate(item.created_at) }}</span>
              <el-button size="small" type="primary">Voir conversation</el-button>
            </div>
          </div>
        </el-card>
      </div>

      <el-empty
        v-else-if="searchQuery.trim() && !isSearching && hasSearched"
        description="Aucune intervention trouvée"
        style="margin-top:1rem;"
      />
    </el-card>

    <div v-if="messages !== null">
      <el-card shadow="never">
        <template #header>
          <div style="display:flex;align-items:center;justify-content:space-between;">
            <div>
              <span class="card-title">Conversation</span>
              <div style="font-size:.8rem;color:var(--el-text-color-secondary);margin-top:.2rem;">
                {{ messages.length }} message{{ messages.length !== 1 ? 's' : '' }}
                <span v-if="loadedForId"> — intervention <code style="font-size:.75rem;">{{ loadedForId.slice(0, 8).toUpperCase() }}</code></span>
              </div>
            </div>
            <el-button :loading="isLoading" text @click="loadMessages(loadedForId)">
              <el-icon><Refresh /></el-icon> Actualiser
            </el-button>
          </div>
        </template>

        <el-empty v-if="!messages.length" description="Aucun message pour cette intervention" />

        <div v-else style="display:flex;flex-direction:column;gap:.75rem;padding:.5rem 0;">
          <div
            v-for="m in messages"
            :key="m.id"
            :style="{
              display: 'flex',
              justifyContent: senderRole(m) === 'ARTISAN' ? 'flex-end' : 'flex-start',
            }"
          >
            <div style="max-width:70%;">
              <div :style="{ display:'flex', alignItems:'center', gap:'.375rem', marginBottom:'.25rem', justifyContent: senderRole(m) === 'ARTISAN' ? 'flex-end' : 'flex-start' }">
                <el-tag
                  :type="senderRole(m) === 'ARTISAN' ? 'primary' : 'success'"
                  size="small"
                >{{ senderRole(m) }}</el-tag>
                <span style="font-size:.75rem;font-weight:700;color:var(--el-text-color-primary);">{{ m.sender_name }}</span>
              </div>

              <div
                :style="{
                  background: senderRole(m) === 'ARTISAN' ? 'var(--el-color-primary-light-9)' : 'var(--el-bg-color)',
                  border: '1.5px solid var(--el-border-color)',
                  borderRadius: senderRole(m) === 'ARTISAN' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                  padding: '.625rem .875rem',
                }"
              >
                <div v-if="m.text" style="font-size:.875rem;line-height:1.55;white-space:pre-wrap;">{{ m.text }}</div>
                <div v-if="m.image_url" style="margin-top:.375rem;">
                  <el-image
                    :src="m.image_url"
                    :preview-src-list="chatImages"
                    :initial-index="chatImages.indexOf(m.image_url)"
                    :preview-teleported="true"
                    hide-on-click-modal
                    fit="cover"
                    style="max-width:200px;max-height:200px;border-radius:8px;cursor:pointer;"
                    alt="Image de la conversation"
                  />
                </div>
              </div>

              <div
                style="font-size:.68rem;color:var(--el-text-color-secondary);margin-top:.2rem;"
                :style="{ textAlign: senderRole(m) === 'ARTISAN' ? 'right' : 'left' }"
              >
                {{ fmtTime(m.created_at) }}
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import { fetchChatMessages, searchChatInterventions } from '../services/adminApi'
import { extractApiError } from '../api'

export default {
  name: 'ChatViewer',
  components: { Search, Refresh },
  props: {
    initialInterventionId: { type: String, default: '' },
  },
  setup(props) {
    const searchQuery   = ref('')
    const searchResults = ref([])
    const isSearching   = ref(false)
    const hasSearched   = ref(false)
    const messages      = ref(null)
    const isLoading     = ref(false)
    const loadedForId   = ref('')
    let debounceTimer   = null

    const senderRole = (m) => {
      const r = (m.sender_role || '').toLowerCase()
      if (r === 'artisan') return 'ARTISAN'
      if (r === 'admin')   return 'ADMIN'
      return 'CLIENT'
    }

    const fmtTime = (d) => d
      ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
      : '—'

    const fmtDate = (d) => d
      ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—'

    const interventionTagType = (s) => {
      const map = {
        PROPOSED: 'info', ACCEPTED: '', EN_ROUTE: 'warning', ARRIVED: 'warning',
        QUOTE_PENDING: '', WORKING: 'primary', PENDING_CLOSURE: 'warning',
        DONE: 'success', CANCELLED: 'danger',
      }
      return map[(s || '').toUpperCase()] || ''
    }

    const onSearchInput = () => {
      clearTimeout(debounceTimer)
      const q = searchQuery.value.trim()
      if (!q) {
        searchResults.value = []
        hasSearched.value = false
        return
      }
      debounceTimer = setTimeout(async () => {
        isSearching.value = true
        hasSearched.value = true
        try {
          searchResults.value = await searchChatInterventions(q)
        } catch (e) {
          ElMessage.error(extractApiError(e))
        } finally {
          isSearching.value = false
        }
      }, 400)
    }

    const selectIntervention = (interventionId) => {
      loadMessages(interventionId)
    }

    const loadMessages = async (id) => {
      if (!id) return
      isLoading.value = true
      try {
        messages.value = await fetchChatMessages(id)
        loadedForId.value = id
      } catch (e) {
        ElMessage.error(extractApiError(e))
      } finally {
        isLoading.value = false
      }
    }

    if (props.initialInterventionId) loadMessages(props.initialInterventionId)

    const chatImages = computed(() => {
      if (!messages.value) return []
      return messages.value.filter(m => m.image_url).map(m => m.image_url)
    })

    return {
      searchQuery, searchResults, isSearching, hasSearched,
      messages, isLoading, loadedForId,
      senderRole, fmtTime, fmtDate, interventionTagType,
      onSearchInput, selectIntervention, loadMessages,
      chatImages,
    }
  }
}
</script>
